import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface TikTokItem {
  link: string;
  img: string;
  title?: string; // opcional
}

@Component({
  selector: 'app-tiktok-carousel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tik-tok-carousel.component.html',
  styleUrl: './tik-tok-carousel.component.scss',
})
export class TikTokCarouselComponent implements AfterViewInit {
  @Input({ required: true }) items: TikTokItem[] = [];
  @Input() title?: string;
  @Input() showDots = true;

  @ViewChild('track', { static: true }) trackRef!: ElementRef<HTMLElement>;

  pageIndex = 0;
  pages: number[] = [];

  private pointerDown = false;
  private startX = 0;
  private startScrollLeft = 0;
  // agrega estas vars
  private dragging = false;
  private dragThreshold = 8; // px (ajusta si quieres)

  ngAfterViewInit(): void {
    this.recalcPages();
    queueMicrotask(() => this.syncPageIndex());
  }

  @HostListener('window:resize')
  onResize() {
    this.recalcPages();
    this.syncPageIndex();
  }

  private get trackEl() {
    return this.trackRef.nativeElement;
  }

  private get perView(): number {
    // según breakpoints (coincide con CSS)
    const w = window.innerWidth;
    if (w <= 520) return 1;
    if (w <= 820) return 2;
    if (w <= 1100) return 3;
    return 4;
  }

  private recalcPages() {
    const pv = Math.max(1, Math.floor(this.perView));
    const pagesCount = Math.max(1, Math.ceil(this.items.length / pv));
    this.pages = Array.from({ length: pagesCount }, (_, i) => i);
  }

  prev() {
    this.goToPage(Math.max(0, this.pageIndex - 1));
  }
  next() {
    this.goToPage(Math.min(this.pages.length - 1, this.pageIndex + 1));
  }

  goToPage(page: number) {
    const pv = Math.max(1, Math.floor(this.perView));
    const idx = page * pv;
    const cards = this.trackEl.querySelectorAll<HTMLElement>('.card');
    const el = cards[idx];
    if (el)
      el.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
        block: 'nearest',
      });
  }

  onScroll() {
    this.syncPageIndex();
  }

  private syncPageIndex() {
    const el = this.trackEl;
    const pv = Math.max(1, Math.floor(this.perView));
    const cards = Array.from(el.querySelectorAll<HTMLElement>('.card'));
    if (!cards.length) return;

    const left = el.getBoundingClientRect().left;
    let bestIdx = 0;
    let bestDist = Infinity;

    for (let i = 0; i < cards.length; i++) {
      const d = Math.abs(cards[i].getBoundingClientRect().left - left);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }

    this.pageIndex = Math.floor(bestIdx / pv);
  }



  onPointerDown(ev: PointerEvent) {
    // solo botón izquierdo en desktop
    if (ev.pointerType === 'mouse' && ev.button !== 0) return;

    this.pointerDown = true;
    this.dragging = false;
    this.startX = ev.clientX;
    this.startScrollLeft = this.trackEl.scrollLeft;
    // OJO: NO hacemos setPointerCapture aquí
  }

  onPointerMove(ev: PointerEvent) {
    if (!this.pointerDown) return;

    const dx = ev.clientX - this.startX;

    // si ya pasó el umbral, ahora sí entramos en modo drag
    if (!this.dragging && Math.abs(dx) > this.dragThreshold) {
      this.dragging = true;
      this.trackEl.setPointerCapture(ev.pointerId);
    }

    if (this.dragging) {
      ev.preventDefault(); // importante para que no seleccione texto / no intente click
      this.trackEl.scrollLeft = this.startScrollLeft - dx;
    }
  }

  onPointerUp() {
    this.pointerDown = false;

    // resetea drag después de un tick para no interferir con el click
    setTimeout(() => (this.dragging = false), 0);
  }

  open(url: string) {
    window.open(url, '_blank');
  }
}
